'use client'

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { LOADING_ANIMATION_TIMING } from '@/shared/constants/animations'
import { cn } from '@/utils/shadcn/utils'

interface SegmentChartSkeletonProps {
  className?: string
  segmentCount?: number
  animationDuration?: number
}

export function SegmentChartSkeleton({
  className = '',
  segmentCount = 6,
  animationDuration = LOADING_ANIMATION_TIMING.SEGMENT_CHART_DURATION,
}: SegmentChartSkeletonProps) {
  // Initialize with static values for SSR
  const initialSegments = Array.from({ length: segmentCount }, () => {
    // Create deterministic initial segments that sum to 100
    const baseValue = 100 / segmentCount
    return Math.round(baseValue * 10) / 10
  })
  const [segments, setSegments] = useState<number[]>(initialSegments)
  const [isClient, setIsClient] = useState(false)

  // Set client flag after mount
  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Only animate on client side
    if (!isClient) return

    // Generate random segment widths that sum to 100%
    const generateSegments = () => {
      const newSegments: number[] = []
      let remaining = 100

      for (let i = 0; i < segmentCount - 1; i++) {
        const maxWidth = remaining - (segmentCount - i - 1) * 5 // Ensure minimum 5% per segment
        const width = Math.random() * (maxWidth - 5) + 5
        const roundedWidth = Math.round(width * 10) / 10 // Round to 1 decimal place
        newSegments.push(roundedWidth)
        remaining -= roundedWidth
      }

      // Last segment gets the remaining width, ensure it's positive
      const lastSegment = Math.max(0, Math.round(remaining * 10) / 10)
      newSegments.push(lastSegment)

      // Ensure the sum is exactly 100% by adjusting the largest segment if needed
      const total = newSegments.reduce((sum, segment) => sum + segment, 0)
      if (Math.abs(total - 100) > 0.1) {
        const largestIndex = newSegments.indexOf(Math.max(...newSegments))
        newSegments[largestIndex] =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          Math.round((newSegments[largestIndex]! + (100 - total)) * 10) / 10
      }
      setSegments(newSegments)
    }

    // Initial generation
    generateSegments()

    // Regenerate segments periodically
    const interval = setInterval(generateSegments, animationDuration)

    return () => clearInterval(interval)
  }, [segmentCount, animationDuration, isClient])

  const colors = [
    'bg-moment-blue-grey-300/60 dark:bg-moment-grey-700/60',
    'bg-moment-blue-grey-400/60 dark:bg-moment-grey-600/60',
    'bg-moment-blue-grey-500/60 dark:bg-moment-grey-500/60',
    'bg-moment-blue-grey-600/60 dark:bg-moment-grey-400/60',
    'bg-moment-blue-grey-700/60 dark:bg-moment-grey-300/60',
    'bg-moment-blue-grey-800/60 dark:bg-moment-grey-200/60',
  ]

  return (
    <div className={cn('relative mt-2.5', className)}>
      {/* Segmented bar skeleton */}
      <div className="flex h-6 rounded-full overflow-hidden">
        {segments.map((width, index) => (
          <motion.div
            key={index}
            className={cn('h-full', colors[index % colors.length])}
            style={{ width: `${width}%` }}
            initial={{ width: `${width}%` }}
            animate={{ width: `${width + 10}%` }}
            transition={{
              duration: 0.8,
              delay: index * 0.1,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            {/* Subtle glow effect */}
            <motion.div
              className="h-full w-full opacity-0"
              animate={{
                opacity: [0, 0.3, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: index * 0.2,
                ease: 'easeInOut',
              }}
            />
          </motion.div>
        ))}
      </div>

      {/* Legend skeleton */}
      <div className="grid grid-cols-2 gap-4 mt-3 w-full">
        {Array.from({ length: 4 }, (_, index) => (
          <motion.div
            key={index}
            className="flex items-center gap-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: 0.5 + index * 0.1,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            {/* Color swatch */}
            <div
              className={cn(
                'w-3 h-3 rounded-sm',
                colors[index % colors.length],
              )}
            />
            {/* Label skeleton */}
            <div className="h-3 w-16 bg-moment-grey-200 dark:bg-moment-grey-800/80 rounded" />
            {/* Percentage skeleton */}
            <div className="h-3 w-8 bg-moment-grey-200 dark:bg-moment-grey-800/80 rounded" />
          </motion.div>
        ))}
      </div>
    </div>
  )
}
